// ==========================================================================
// Squarebit Store - Copyright (c) 2026 Squarebit LLC. All rights reserved.
// Proprietary and confidential. Not for redistribution.
//
// Squarebit Eye product pages / widget (store front end). The Eye software
// itself is licensed separately under the Squarebit Eye End User License
// Agreement: https://store.squarebitstudios.com/squarebit-eye/eula
// ==========================================================================

import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CodeBlock, MenuPath, Note, Tip, VarCard } from '../../touchpose/ui';
import { H2, H3, Body, DocHero, DocFooter, UL_CLASS } from './shared';

// Everything an artist reads is keyed under eyeDocs.textures.*. CLI flag
// names, file names and the baked example command are identifiers: they stay
// outside the translated strings (components slots / raw CodeBlock children).
const LINK = 'text-blue-link no-underline font-medium';

const TexturesDoc: React.FC = () => {
  const { t } = useTranslation();

  return (
    <article className="font-body text-content-primary max-w-[760px]">
      <DocHero
        title={t('eyeDocs.textures.title')}
        intro={t('eyeDocs.textures.intro')}
      />

      <H2 id="iris-map">{t('eyeDocs.textures.h2IrisMap')}</H2>
      <Body>
        <Trans i18nKey="eyeDocs.textures.irisIntro" components={{ i: <em /> }} />
      </Body>
      <ul className={UL_CLASS}>
        <li>
          <Trans
            i18nKey="eyeDocs.textures.irisB1"
            components={{ b: <strong /> }}
          />
        </li>
        <li>
          <Trans
            i18nKey="eyeDocs.textures.irisB2"
            components={{ b: <strong /> }}
          />
        </li>
        <li>
          <Trans
            i18nKey="eyeDocs.textures.irisB3"
            values={{ dial: t('squarebitEye.irisDepthAoLabel') }}
            components={{
              b: <strong />,
              ao: (
                <Link
                  to="/squarebit-eye/docs/parameters#iris-pupil"
                  className={LINK}
                />
              )
            }}
          />
        </li>
        <li>{t('eyeDocs.textures.irisB4')}</li>
      </ul>
      <Note label={t('eyeDocs.textures.noteRingLabel')}>
        <Trans
          i18nKey="eyeDocs.textures.noteRing"
          components={{
            limbus: (
              <Link to="/squarebit-eye/docs/parameters#limbus" className={LINK} />
            )
          }}
        />
      </Note>

      <H2 id="sclera-map">{t('eyeDocs.textures.h2ScleraMap')}</H2>
      <Body>{t('eyeDocs.textures.scleraIntro')}</Body>
      <ul className={UL_CLASS}>
        <li>
          <Trans
            i18nKey="eyeDocs.textures.scleraB1"
            components={{ b: <strong /> }}
          />
        </li>
        <li>
          <Trans
            i18nKey="eyeDocs.textures.scleraB2"
            components={{ uv: <strong>UV0</strong> }}
          />
        </li>
        <li>{t('eyeDocs.textures.scleraB3')}</li>
      </ul>

      <H2 id="limbus-map">{t('eyeDocs.textures.h2LimbusMap')}</H2>
      <Body>{t('eyeDocs.textures.limbusIntro')}</Body>
      <ul className={UL_CLASS}>
        <li>
          <Trans
            i18nKey="eyeDocs.textures.limbusB1"
            components={{ b: <strong /> }}
          />
        </li>
        <li>{t('eyeDocs.textures.limbusB2')}</li>
        <li>
          <Trans
            i18nKey="eyeDocs.textures.limbusB3"
            components={{
              limbus: (
                <Link
                  to="/squarebit-eye/docs/parameters#limbus"
                  className={LINK}
                />
              )
            }}
          />
        </li>
      </ul>

      <H2 id="surface-map">{t('eyeDocs.textures.h2SurfaceMap')}</H2>
      <Body>{t('eyeDocs.textures.surfaceIntro')}</Body>
      <ul className={UL_CLASS}>
        <li>
          <Trans
            i18nKey="eyeDocs.textures.surfaceB1"
            components={{ b: <strong /> }}
          />
        </li>
        <li>
          <Trans
            i18nKey="eyeDocs.textures.surfaceB2"
            components={{ b: <strong /> }}
          />
        </li>
        <li>
          <Trans
            i18nKey="eyeDocs.textures.surfaceB3"
            components={{ b: <strong /> }}
          />
        </li>
        <li>
          <Trans
            i18nKey="eyeDocs.textures.surfaceB4"
            components={{
              surface: (
                <Link
                  to="/squarebit-eye/docs/parameters#cornea"
                  className={LINK}
                />
              )
            }}
          />
        </li>
      </ul>

      <H2 id="baking">{t('eyeDocs.textures.h2Baking')}</H2>
      <Body>
        <Trans
          i18nKey="eyeDocs.textures.bakingIntro"
          components={{ code: <MenuPath>bake_iris.py</MenuPath> }}
        />
      </Body>
      <CodeBlock language="bash">
        {`# a green eye with freckles and a blue heterochromia wedge
python bake_iris.py iris_green.png --size 1024 \\
    --inner 0.47,0.28,0.10 --outer 0.46,0.62,0.47 \\
    --fiber-style 1 --fiber 0.7 --fiber-waviness 0.3 --fiber-clumping 0.4 \\
    --fiber-crypts 0.45 \\
    --spot-density 0.4 --spot-irregularity 0.5 --spot-color-vary 0.4 \\
    --hetero-amount 0.25 --hetero-angle 40 --hetero-color 0.35,0.28,0.53

# a matching plain white sclera
python bake_iris.py sclera.png --sclera`}
      </CodeBlock>
      <H3>{t('eyeDocs.textures.h3Flags')}</H3>
      <VarCard
        name="out / --size / --sclera"
        type="path / px / flag"
        example="iris.png --size 512"
      >
        <Trans
          i18nKey="eyeDocs.textures.flagOut"
          components={{ f: <strong>--sclera</strong> }}
        />
      </VarCard>
      <VarCard
        name="--inner / --outer / --spot-color / --hetero-color"
        type="R,G,B (0–1)"
        example="0.46,0.62,0.47"
      >
        {t('eyeDocs.textures.flagColors')}
      </VarCard>
      <VarCard
        name="--detail / --fiber / --fiber-style / --fiber-waviness / --fiber-clumping / --fiber-gaps / --fiber-crypts"
        type="floats / 0|1|2"
        example="--fiber-style 1"
      >
        <Trans
          i18nKey="eyeDocs.textures.flagFibers"
          values={{
            wispy: t('squarebitEye.fiberWispy'),
            billow: t('squarebitEye.noiseBillow'),
            ridged: t('squarebitEye.noiseRidged')
          }}
          components={{ f: <strong>--fiber-style</strong> }}
        />
      </VarCard>
      <VarCard name="--halo-size / --halo-strength" type="float" example="1.0">
        {t('eyeDocs.textures.flagHalo')}
      </VarCard>
      <VarCard
        name="--spot-density / --spot-scale / --spot-spread / --spot-seed / --spot-irregularity / --spot-opacity / --spot-color-vary"
        type="floats / int seed"
        example="--spot-density 0.4"
      >
        {t('eyeDocs.textures.flagSpots')}
      </VarCard>
      <VarCard
        name="--hetero-amount / --hetero-angle / --hetero-softness"
        type="0–1 / degrees / float"
        example="--hetero-amount 0.25"
      >
        {t('eyeDocs.textures.flagHetero')}
      </VarCard>
      <VarCard name="--ref-pupil" type="float" example="0.342">
        {t('eyeDocs.textures.flagRefPupil')}
      </VarCard>
      <Body>
        <Trans
          i18nKey="eyeDocs.textures.everyFlag"
          values={{ group: t('eyeDocs.layout.proceduralIris') }}
          components={{
            proc: (
              <Link
                to="/squarebit-eye/docs/parameters#procedural-iris"
                className={LINK}
              />
            )
          }}
        />
      </Body>
      <Tip label={t('eyeDocs.textures.tipLayersLabel')}>
        <Trans
          i18nKey="eyeDocs.textures.tipLayers"
          values={{ button: t('squarebitEye.downloadIrisLayers') }}
          components={{ b: <strong /> }}
        />
      </Tip>

      <DocFooter current="/squarebit-eye/docs/textures" />
    </article>
  );
};

export default TexturesDoc;
